<template>
    <div class="admin-instance-page">
        <div class="nk-block-head nk-block-head-sm">
            <div class="nk-block-between align-items-start">
                <div class="nk-block-head-content admin-instance-list__intro">
                    <div class="admin-instance-list__title-row">
                        <h3 class="nk-block-title page-title mb-0">{{trans('subscription.subscription')}}</h3>
                    </div>
                    <div class="nk-block-des text-soft" v-if="!subscriptions.total">
                        <p>{{ trans('subscription.module_info_description') }}</p>
                    </div>
                </div>
                <div class="nk-fmg-actions flex-shrink-0">
                    <ul class="nk-block-tools g-2 flex-wrap justify-content-end">
                        <li><a href="#" class="btn btn-sm admin-toolbar-btn admin-toolbar-btn--outline" @click.prevent="showFilterPanel = !showFilterPanel"><admin-icon name="sliders"></admin-icon> <span>{{trans('general.filter')}}</span></a></li>
                    </ul>
                </div>
            </div>
        </div>

        <transition name="fade">
            <div class="card admin-instance-form-card mb-2" v-if="showFilterPanel">
                <div class="card-body">
                    <div class="admin-instance-card-head">
                        <h4 class="admin-instance-card-head__title">{{trans('general.filter')}}</h4>
                        <p class="admin-instance-card-head__hint">{{ trans('subscription.subscription') }}</p>
                    </div>
                    <div class="row">
                        <div class="col-12 col-sm-3">
                            <div class="form-group">
                                <label for="">{{trans('subscription.status')}}</label>
                                <select name="status" class="form-control" v-model="filterSubscriptionForm.status">
                                    <option value="">{{trans('general.select_one')}}</option>
                                    <option value=0>{{trans('subscription.failed')}}</option>
                                    <option value=1>{{trans('subscription.complete')}}</option>
                                </select>
                            </div>
                        </div>
                        <div class="col-12 col-sm-6">
                            <div class="form-group">
                                <date-range-picker :start-date.sync="filterSubscriptionForm.start_date" :end-date.sync="filterSubscriptionForm.end_date" :label="trans('general.date_between')"></date-range-picker>
                            </div>
                        </div>
                        <div class="col-12 col-sm-3">
                            <div class="form-group">
                                <label for="">{{trans('general.sort_by')}}</label>
                                <select name="order" class="form-control" v-model="filterSubscriptionForm.sort_by">
                                    <option value="created_at">{{trans('subscription.created_at')}}</option>
                                    <option value="status">{{trans('subscription.status')}}</option>
                                </select>
                            </div>
                        </div>
                        <div class="col-12 col-sm-3">
                            <div class="form-group">
                                <label for="">{{trans('general.order')}}</label>
                                <select name="order" class="form-control" v-model="filterSubscriptionForm.order">
                                    <option value="asc">{{trans('general.ascending')}}</option>
                                    <option value="desc">{{trans('general.descending')}}</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
        
        <div class="admin-list-table-card mt-2" v-if="hasPermission('list-subscriber') && subscriptions.total">
            <div class="table-responsive">
                <table class="table admin-data-table admin-data-table--config-list mb-0">
                    <thead>
                        <tr>
                            <th>{{trans('subscription.number')}}</th>
                            <th>{{trans('subscription.email')}}</th>
                            <th>{{trans('subscription.domain')}}</th>
                            <th>{{trans('subscription.amount')}}</th>
                            <th>{{trans('subscription.method')}}</th>
                            <th>{{trans('plan.plan')}}</th>
                            <sortable-th field="status" :sort-by="filterSubscriptionForm.sort_by" :order="filterSubscriptionForm.order" @sort="sortList">{{trans('subscription.status')}}</sortable-th>
                            <sortable-th field="created_at" :sort-by="filterSubscriptionForm.sort_by" :order="filterSubscriptionForm.order" @sort="sortList">{{trans('subscription.created_at')}}</sortable-th>
                            <th class="admin-table-actions-cell">{{trans('general.actions')}}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="subscription in subscriptions.data" :key="subscription.id">
                            <td>
                                <span v-text="getNumber(subscription)"></span>
                                <p class="mb-0"><small>{{ subscription.uuid }}</small></p>
                            </td>
                            <td v-text="subscription.instance.email"></td>
                            <td><code class="admin-instance-meta__code">{{ subscription.instance.domain }}</code></td>
                            <td>{{formatCurrency(subscription.amount,subscription.currency)}}</td>
                            <td>{{subscription.source === 'paypal' ? 'Paypal' : 'Credit Card'}}</td>
                            <td v-text="subscription.plan.name"></td>
                            <td>
                                <span v-if="subscription.status" class="admin-status-badge admin-status-badge--success">{{trans('subscription.complete')}}</span>
                                <span v-else class="admin-status-badge admin-status-badge--danger">{{trans('subscription.failed')}}</span>
                            </td>
                            <td>{{subscription.created_at | moment }}</td>
                            <td class="admin-table-actions-cell">
                                <table-row-action-dropdown>
                                    <b-dropdown-item
                                        v-if="subscription.status"
                                        :href="`/subscription/${subscription.uuid}/print?token=${authToken}`"
                                        target="_blank"
                                    >
                                        <admin-icon name="printer"></admin-icon> {{ trans('subscription.print') }}
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        v-if="subscription.status"
                                        :href="`/subscription/${subscription.uuid}/pdf?token=${authToken}`"
                                        target="_blank"
                                    >
                                        <admin-icon name="file"></admin-icon> {{ trans('subscription.pdf') }}
                                    </b-dropdown-item>
                                    <b-dropdown-item-button
                                        v-if="!subscription.status"
                                        @click="fetchDetail(subscription)"
                                    >
                                        <admin-icon name="eye"></admin-icon> {{ trans('subscription.view') }}
                                    </b-dropdown-item-button>
                                    <b-dropdown-item-button
                                        v-if="hasPermission('delete-subscription') && !subscription.status"
                                        v-confirm="{ok: confirmDelete(subscription)}"
                                        class="admin-row-actions__danger"
                                    >
                                        <admin-icon name="trash"></admin-icon> {{ trans('subscription.delete_subscription') }}
                                    </b-dropdown-item-button>
                                </table-row-action-dropdown>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="admin-table-pagination-wrap">
                <pagination-record :page-length.sync="filterSubscriptionForm.page_length" :records="subscriptions" @updateRecords="getSubscriptions" @change.native="getSubscriptions"></pagination-record>
            </div>
        </div>
            
        <div class="modal fade subscription-detail admin-modal-pro" tabindex="-1" role="dialog" aria-labelledby="subscriptionDetail" aria-hidden="true">
            <div class="modal-dialog modal-lg">
                <div class="modal-content admin-modal-pro__content">
                    <div class="modal-header">
                        <h4 class="modal-title" id="subscriptionDetail">{{trans('subscription.subscription_detail')}} <span class="admin-status-badge admin-status-badge--danger">{{trans('subscription.failed')}}</span></h4>
                        <button type="button" class="close" data-dismiss="modal" aria-hidden="true">×</button>
                    </div>
                    <div class="modal-body" v-if="subscription">
                        <div class="row">
                            <div class="col-12 col-sm-6">
                                <table class="table no-border">
                                    <tr><th>{{trans('subscription.type')}}</th><th class="font-medium">{{subscription.type === 'extend' ? 'Validity Extend' : 'Upgrade'}}</th>
                                    </tr>
                                    <tr><th>{{trans('subscription.date')}}</th><th class="font-medium">{{subscription.created_at | momentDateTime}}</th>
                                    </tr>
                                    <tr><th>{{trans('instance.email')}}</th><th class="font-medium" v-if="subscription.instance">{{subscription.instance.email}}</th>
                                    </tr>
                                    <tr><th>{{trans('instance.domain')}}</th><th v-if="subscription.instance">{{subscription.instance.domain}}</th>
                                    </tr>
                                    <tr><th>{{trans('plan.plan')}}</th><th class="font-medium" v-if="subscription.plan">{{subscription.plan.name}}</th>
                                    </tr>
                                    <tr><th>{{trans('subscription.validity')}}</th><th class="font-medium">{{subscription.validity | moment}}</th>
                                    </tr>
                                    <tr><th>{{trans('subscription.amount')}}</th><th class="font-medium" v-if="subscription.currency">{{formatCurrency(subscription.amount,subscription.currency)}}</th>
                                    </tr>
                                    <tr><th>{{trans('subscription.fail_reason')}}</th><th class="font-medium">{{subscription.gateway_status || '-'}}</th>
                                    </tr>
                                </table>
                            </div>
                            <div class="col-12 col-sm-6">
                                <table class="table no-border">
                                    <tr><th>{{trans('user.address_line_1')}}</th><th class="font-medium">{{subscription.address_line_1}}</th>
                                    </tr>
                                    <tr><th>{{trans('user.address_line_2')}}</th><th class="font-medium">{{subscription.address_line_2}}</th>
                                    </tr>
                                    <tr><th>{{trans('user.city')}}</th><th class="font-medium">{{subscription.city}}</th>
                                    </tr>
                                    <tr><th>{{trans('user.state')}}</th><th class="font-medium">{{subscription.state}}</th>
                                    </tr>
                                    <tr><th>{{trans('user.zipcode')}}</th><th class="font-medium">{{subscription.zipcode}}</th>
                                    </tr>
                                    <tr><th>{{trans('user.country')}}</th><th class="font-medium">{{countries[subscription.country]}}</th>
                                    </tr>
                                    <tr><th>IP</th><th class="font-medium">{{subscription.ip}}</th>
                                    </tr>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-sm admin-toolbar-btn admin-toolbar-btn--outline" data-dismiss="modal">{{trans('general.close')}}</button>
                    </div>
                </div>
            </div>
        </div>

        <div class="admin-list-table-card admin-empty-state mt-3" v-if="!subscriptions.total" role="status">
            <div class="admin-empty-state__inner">
                <div class="admin-empty-state__icon-wrap" aria-hidden="true">
                    <admin-icon name="receipt"></admin-icon>
                </div>
                <h4 class="admin-empty-state__title">{{ trans('subscription.empty_list_title') }}</h4>
                <p class="admin-empty-state__text">{{ trans('subscription.empty_list_hint') }}</p>
            </div>
        </div>
    </div>
</template>
<script>
    import dateRangePicker from '../../components/date-range-picker'

    export default {
        components: {dateRangePicker},
        data(){
            return {
                subscriptions: {
                    total: 0,
                    data: []
                },
                filterSubscriptionForm: {
                    status: '',
                    start_date: '',
                    end_date: '',
                    order: 'desc',
                    sort_by: 'created_at',
                    page_length: helper.getConfig('page_length')
                },
                countries: [],
                subscription: '',
                showFilterPanel: false
            };
        },
        mounted(){
            this.getSubscriptions();
        },
        methods: {
            getSubscriptions(page){
                if (typeof page !== 'number') {
                    page = 1;
                }
                let url = helper.getFilterURL(this.filterSubscriptionForm);
                axios.get('/api/subscription?page=' + page + url)
                    .then(response => response.data)
                    .then(response => this.subscriptions = response)
                    .catch(error => {
                        helper.showDataErrorMsg(error);
                    });
            },
            getNumber(subscription){
                return (subscription.number) ?  subscription.prefix+''+helper.formatWithPadding(subscription.number,helper.getConfig('subscription_invoice_number_digit')) : '';
            },
            fetchDetail(subscription){
                axios.get('/api/subscription/'+subscription.uuid)
                    .then(response => response.data)
                    .then(response => {
                        this.subscription = response.subscription;
                        this.countries = response.countries;
                        this.$nextTick(() => {
                            if (window.$) {
                                window.$('.subscription-detail').modal('show');
                            }
                        });
                    })
                    .catch(error => {
                        helper.showDataErrorMsg(error);
                    })
            },
            confirmDelete(subscription){
                return dialog => this.deleteSubscription(subscription);
            },
            deleteSubscription(subscription){
                axios.delete('/api/subscription/'+subscription.id)
                    .then(response => response.data)
                    .then(response => {
                        toastr.success(response.message);
                        this.getSubscriptions();
                    }).catch(error => {
                        helper.showDataErrorMsg(error);
                    });
            },
            formatCurrency(amount, currency){
                return helper.formatCurrency(amount,currency);
            },
            hasPermission(permission){
                return helper.hasPermission(permission);
            },
            sortList(field){
                if (this.filterSubscriptionForm.sort_by === field) {
                    this.filterSubscriptionForm.order = this.filterSubscriptionForm.order === 'asc' ? 'desc' : 'asc';
                } else {
                    this.filterSubscriptionForm.sort_by = field;
                    this.filterSubscriptionForm.order = 'asc';
                }
            }
        },
        filters: {
          moment(date) {
            return helper.formatDate(date);
          },
          momentDateTime(datetime) {
            return helper.formatDateTime(datetime);
          }
        },
        computed: {
            authToken(){
                return localStorage.getItem('auth_token');
            }
        },
        watch: {
            filterSubscriptionForm: {
                handler(val){
                    this.getSubscriptions();
                },
                deep: true
            }
        }
    }
</script>
